import { proxySet } from "valtio/utils";

import type { Router } from "@/__main__/router.mjs";
import type { ClassNameEntry } from "@/util/class-names.mjs";
import { proxy } from "@/util/proxy.mjs";
import makeStrictKeysObject from "@/util/strict-keys-object.mjs";

interface ComponentRefs {
  HeaderWrapper: React.FC<{ children: React.ReactNode }>;
  NavigationWrapper: React.FC<{ children: React.ReactNode }>;
  ContentWrapper: React.FC<{ children: React.ReactNode }>;
  NonAppNavigation: React.FC<{ children: React.ReactNode }>;
  floatingElementsTop: React.FC[];
  floatingElementsInner: React.FC[];
}

export default makeStrictKeysObject({
  // ===================================================
  // Used in root!
  mountPromise: null,

  // Package version is defined here to avoid import ordering / circular dep issues.
  PKG_VERSION: typeof __PKG_VERSION__ !== "undefined" && __PKG_VERSION__,

  // ===================================================

  externalContainerClasses: proxySet<ClassNameEntry>(),
  internalContainerClasses: proxySet<ClassNameEntry>(),

  components: proxy<ComponentRefs>({
    // Used in App root component
    HeaderWrapper: ({ children }) => children,
    NavigationWrapper: ({ children }) => children,

    // This is primarily used as an entry point for ads to shift the layout.
    ContentWrapper: ({ children }) => children,

    NonAppNavigation: ({ children }) => children,

    floatingElementsTop: [],
    floatingElementsInner: [],
  }),

  appInit: null,
  didRender: null as Promise<void> | null,

  // ===================================================
  // Router
  router: null as Router | null,
  routes: [],
  defaultRoute: null,
  initializeDefaultRoute: null,

  // ===================================================
  // SEO / Meta
  DEFAULT_META_IMAGE: null,
  DEFAULT_SITE: ["common:blitz", "Blitz"] satisfies Translation,

  // ===================================================
  // IPC core + game integrations
  mockBlitzMessage: {},
  mockOnce(event, callback) {
    this.mockBlitzMessage[event] = (_, v) => {
      delete this.mockBlitzMessage[event];
      return callback(v);
    };
  },

  // ===================================================
  // App state
  writeSemaphore: false,
  allowWrite() {
    if (this.writeSemaphore) return;

    this.writeSemaphore = true;

    setTimeout(() => {
      this.writeSemaphore = false;
    }, 0);
  },

  // ===================================================
  // getData
  checkForAuthorization: (_message: string, _url: string): boolean => false,
  recentURLs: new Set(),
  ERROR_BACKOFF_TIME: 0,
  DEFAULT_BACKOFF_TIME: 0,

  // ===================================================
  // Data model
  enableAccessWarnings: true,
});

export const EVENTS = makeStrictKeysObject({
  EVENT_OVERLAY_BROADCAST: "EVENT_OVERLAY_BROADCAST",
  EVENT_APP_BROADCAST: "EVENT_APP_BROADCAST",
});
